import {
	ChangeEvent,
	FocusEvent,
	forwardRef,
	MouseEvent,
	useCallback,
	useMemo,
	useRef,
	useState,
	WheelEventHandler,
} from 'react';
import {
	IconButton,
	InputAdornment,
	TextField,
	TextFieldProps,
} from '@mui/material';

import { CrossIcon } from '../../icons';

type TMplInputV2Props = TextFieldProps & {
	clearable?: boolean;
};

export const MplInputV2 = forwardRef<HTMLInputElement, TMplInputV2Props>(
	(props, ref) => {
		const {
			clearable,
			value: valueProp,
			onChange,
			onFocus,
			onBlur,
			InputProps: inputPropsFromParent,
			inputProps: inputElementProps,
			disabled,
			...rest
		} = props;
		const { type } = props;

		const inputRef = useRef<HTMLInputElement>(null);
		const [focused, setFocused] = useState(false);
		const isControlled = valueProp !== undefined;

		const [uncontrolledValue, setUncontrolledValue] = useState('');
		const resolvedValue = (isControlled ? valueProp : uncontrolledValue) ?? '';
		const hasValue =
			resolvedValue !== '' &&
			resolvedValue !== null &&
			resolvedValue !== undefined;
		const readOnly = Boolean(inputElementProps?.readOnly);

		const isClearVisible = Boolean(
			clearable && (focused || hasValue) && !disabled && !readOnly
		);

		const handleChange = useCallback(
			(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
				if (!isControlled) {
					setUncontrolledValue(e.target.value);
				}
				onChange?.(e);
			},
			[isControlled, onChange]
		);

		const handleFocus = useCallback(
			(e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
				setFocused(true);
				onFocus?.(e);
			},
			[onFocus]
		);

		const handleBlur = useCallback(
			(e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
				setFocused(false);
				onBlur?.(e);
			},
			[onBlur]
		);

		const handleClear = useCallback((e: MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();

			if (inputRef?.current) {
				// Update the value by ref
				const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
					window.HTMLInputElement.prototype,
					'value'
				)!.set;
				nativeInputValueSetter!.call(inputRef.current, '');

				// Then trigger the onChange event of the input
				//    for some input (select, textarea) we may want to trigger 'change' instead of 'input' who not listen to input event
				inputRef.current.dispatchEvent(new Event('input', { bubbles: true }));
			}
		}, []);

		/** TODO align with the DS */
		const mergedEndAdornment = useMemo(() => {
			const existing = inputPropsFromParent?.endAdornment;

			if (!existing && !isClearVisible) {
				return null;
			}

			return (
				<>
					{existing}
					{isClearVisible && (
						<InputAdornment position="end">
							<IconButton
								size="small"
								edge="end"
								tabIndex={-1}
								onMouseDown={(e) => {
									e.preventDefault();
								}}
								onClick={handleClear}
							>
								<CrossIcon />
							</IconButton>
						</InputAdornment>
					)}
				</>
			);
		}, [inputPropsFromParent?.endAdornment, isClearVisible, handleClear]);

		/** https://github.com/mui/material-ui/issues/7960 */
		const preventScrollingOnWheel = useCallback<WheelEventHandler>(
			(e) => {
				if (type === 'number') {
					e.target instanceof HTMLElement && e.target.blur();
				}
			},
			[type]
		);

		return (
			<TextField
				{...rest}
				onWheel={preventScrollingOnWheel}
				ref={ref}
				value={resolvedValue}
				onChange={handleChange}
				onFocus={handleFocus}
				onBlur={handleBlur}
				inputRef={inputRef}
				disabled={disabled}
				inputProps={inputElementProps}
				InputProps={{
					...inputPropsFromParent,
					endAdornment: mergedEndAdornment,
				}}
			/>
		);
	}
);
